import { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Building2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const OFFICES = [
  {
    id: 'hq',
    label: 'Headquarters',
    name: 'Red Ball Express — HQ',
    address1: '30 N Gould St #58539',
    address2: 'Sheridan, WY 82801, USA',
    coords: [44.7972, -106.9561],
    color: '#dc2626',
  },
  {
    id: 'west',
    label: 'West Coast Office',
    name: 'Red Ball Express — San Diego',
    address1: '3111 Camino Del Rio North #400',
    address2: 'San Diego, CA 92108, USA',
    coords: [32.7715, -117.1373],
    color: '#dc2626',
  },
];

// US bounds tuned so both pins are framed nicely
const FIT_BOUNDS = [
  [30.5, -120.0],
  [46.5, -103.5],
];

// Build a smooth arc between two latlng points using a quadratic bezier
// with a control point lifted northward to simulate a flight path.
const buildArc = (from, to, segments = 60) => {
  const [lat1, lng1] = from;
  const [lat2, lng2] = to;
  // Midpoint with a "lift" north for the arc apex
  const midLat = (lat1 + lat2) / 2 + 6; // 6° north lift
  const midLng = (lng1 + lng2) / 2;
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const oneMinusT = 1 - t;
    const lat =
      oneMinusT * oneMinusT * lat1 +
      2 * oneMinusT * t * midLat +
      t * t * lat2;
    const lng =
      oneMinusT * oneMinusT * lng1 +
      2 * oneMinusT * t * midLng +
      t * t * lng2;
    points.push([lat, lng]);
  }
  return points;
};

export const ContactMap = () => {
  const [active, setActive] = useState(OFFICES[0].id);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  // Init the Leaflet map once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import('leaflet')).default;
      if (cancelled || !containerRef.current || mapRef.current) return;

      const map = L.map(containerRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
      });

      // Dark theme tiles to match site aesthetic
      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 18,
        }
      ).addTo(map);

      map.fitBounds(FIT_BOUNDS, { padding: [40, 40] });

      // Custom red ball icon
      const buildIcon = (size = 28) =>
        L.divIcon({
          className: 'rbe-map-pin',
          html: `
            <span class="rbe-pin-pulse"></span>
            <span class="rbe-pin-core"></span>
          `,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });

      OFFICES.forEach((office) => {
        const marker = L.marker(office.coords, { icon: buildIcon(), title: office.name })
          .addTo(map)
          .bindPopup(
            `<div style="font-family:Outfit,sans-serif;min-width:200px">
               <div style="font-size:10px;font-weight:700;letter-spacing:0.18em;color:#dc2626;text-transform:uppercase;margin-bottom:2px">${office.label}</div>
               <div style="font-weight:700;color:#18181b;margin-bottom:4px">${office.name}</div>
               <div style="font-size:13px;color:#52525b;line-height:1.4">${office.address1}<br/>${office.address2}</div>
             </div>`
          );
        marker.on('click', () => setActive(office.id));
        markersRef.current[office.id] = marker;
      });

      // Connecting flight-arc between the two offices (great-circle-ish curve via quadratic bezier)
      const arcLatLngs = buildArc(OFFICES[0].coords, OFFICES[1].coords, 60);
      L.polyline(arcLatLngs, {
        color: '#dc2626',
        weight: 2,
        opacity: 0.85,
        dashArray: '6 8',
        className: 'rbe-flight-arc',
      }).addTo(map);

      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
  }, []);

  // Fly to active office when user clicks a card
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const office = OFFICES.find((o) => o.id === active);
    if (!office) return;
    const marker = markersRef.current[office.id];
    map.flyTo(office.coords, 8, { duration: 1.0 });
    if (marker) setTimeout(() => marker.openPopup(), 700);
  }, [active]);

  return (
    <section
      className="bg-white border-t border-zinc-100 py-20"
      style={{ fontFamily: 'Outfit, sans-serif' }}
      data-testid="contact-map-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 bg-red-50 border border-red-100 px-3 py-1.5">
            <Navigation className="w-3.5 h-3.5 text-red-600" />
            <span className="text-red-600 text-[10px] font-bold uppercase tracking-[0.25em]">
              Coast to Coast
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-zinc-900 leading-tight tracking-tight mb-3">
            Two Offices. <span className="text-red-600">One Network.</span>
          </h2>
          <p
            className="text-zinc-500 text-base max-w-xl mx-auto"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Strategically positioned to haul freight for shippers across
            the lower 48.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Office cards */}
          <div className="lg:col-span-4 space-y-4 order-2 lg:order-1">
            {OFFICES.map((office) => {
              const isActive = office.id === active;
              return (
                <button
                  key={office.id}
                  type="button"
                  onClick={() => setActive(office.id)}
                  className={`w-full text-left border transition-all duration-300 p-5 ${
                    isActive
                      ? 'border-red-600 bg-red-50/60 -translate-y-0.5 shadow-sm'
                      : 'border-zinc-200 bg-white hover:border-zinc-400'
                  }`}
                  data-testid={`map-office-card-${office.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive ? 'bg-red-600 text-white' : 'bg-zinc-100 text-zinc-500'
                      }`}
                    >
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-1 ${
                          isActive ? 'text-red-600' : 'text-zinc-500'
                        }`}
                      >
                        {office.label}
                      </div>
                      <div className="font-bold text-zinc-900 mb-1">{office.name}</div>
                      <div
                        className="text-sm text-zinc-600 leading-relaxed"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {office.address1}
                        <br />
                        {office.address2}
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${office.address1}, ${office.address2}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-wider text-red-600 hover:text-red-700"
                        data-testid={`map-directions-${office.id}`}
                      >
                        <MapPin className="w-3 h-3" />
                        Get Directions
                      </a>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Map */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="relative border border-zinc-200 overflow-hidden">
              <div
                ref={containerRef}
                className="w-full bg-zinc-900"
                style={{ height: '420px' }}
                data-testid="contact-map-leaflet"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMap;
